/**
 * Schedule Engine
 * 
 * Real-time quantity extraction and schedule generation.
 * Supports calculated fields, grouping, filtering, and aggregation.
 */

import { v4 as uuidv4 } from 'uuid';

// ==================== Types ====================

export type FieldType = 'String' | 'Number' | 'Area' | 'Length' | 'Volume' | 'Integer' | 'Currency' | 'Boolean';
export type AggregationType = 'None' | 'Sum' | 'Average' | 'Count' | 'Min' | 'Max';
export type SortOrder = 'Ascending' | 'Descending';

export interface ScheduleField {
    id: string;
    name: string;
    parameterName: string;  // Source parameter on element
    fieldType: FieldType;
    isCalculated: boolean;
    formula?: string;
    isHidden: boolean;
    width: number;
    aggregation: AggregationType;
    formatting?: {
        prefix?: string;
        suffix?: string;
        decimalPlaces?: number;
        useThousandsSeparator?: boolean;
    };
}

export interface ScheduleFilter {
    fieldId: string;
    operator: 'Equals' | 'NotEquals' | 'Contains' | 'GreaterThan' | 'LessThan' | 'Between' | 'IsEmpty';
    value: any;
    value2?: any;  // For 'Between'
}

export interface ScheduleSorting {
    fieldId: string;
    order: SortOrder;
}

export interface ScheduleGrouping {
    fieldId: string;
    showHeader: boolean;
    showFooter: boolean;
    showBlankRow: boolean;
}

export interface ScheduleDefinition {
    id: string;
    name: string;
    category: string;
    fields: ScheduleField[];
    filters: ScheduleFilter[];
    sorting: ScheduleSorting[];
    grouping: ScheduleGrouping[];
    showGrandTotal: boolean;
    includeElementsInLinkedFiles: boolean;
}

export interface ScheduleRow {
    elementId: string;
    values: Record<string, any>;
    isGroupHeader?: boolean;
    isGroupFooter?: boolean;
    isGrandTotal?: boolean;
    groupLevel?: number;
}

export interface ScheduleResult {
    definition: ScheduleDefinition;
    rows: ScheduleRow[];
    totals: Record<string, number>;
    rowCount: number;
    generatedAt: number;
}

// ==================== Schedule Engine ====================

export class ScheduleEngine {
    private schedules: Map<string, ScheduleDefinition> = new Map();
    private elementSource: () => any[];  // Function to get elements

    constructor(elementSource: () => any[]) {
        this.elementSource = elementSource;
    }

    // ==================== Schedule Definition ====================

    public createSchedule(
        name: string,
        category: string,
        fields?: Partial<ScheduleField>[]
    ): ScheduleDefinition {
        const schedule: ScheduleDefinition = {
            id: uuidv4(),
            name,
            category,
            fields: [],
            filters: [],
            sorting: [],
            grouping: [],
            showGrandTotal: true,
            includeElementsInLinkedFiles: false
        };

        // Add default fields
        this.addField(schedule, { name: 'Type', parameterName: 'type', fieldType: 'String' });
        this.addField(schedule, { name: 'Count', parameterName: '__count__', fieldType: 'Integer', isCalculated: true, aggregation: 'Count' });

        // Add custom fields
        if (fields) {
            for (const field of fields) {
                this.addField(schedule, field);
            }
        }

        this.schedules.set(schedule.id, schedule);
        return schedule;
    }

    public addField(schedule: ScheduleDefinition, fieldDef: Partial<ScheduleField>): ScheduleField {
        const field: ScheduleField = {
            id: uuidv4(),
            name: fieldDef.name || 'Field',
            parameterName: fieldDef.parameterName || '',
            fieldType: fieldDef.fieldType || 'String',
            isCalculated: fieldDef.isCalculated || false,
            formula: fieldDef.formula,
            isHidden: fieldDef.isHidden || false,
            width: fieldDef.width || 100,
            aggregation: fieldDef.aggregation || 'None',
            formatting: fieldDef.formatting
        };

        schedule.fields.push(field);
        return field;
    }

    public addFilter(scheduleId: string, filter: Omit<ScheduleFilter, 'id'> & { fieldId: string }): void {
        const schedule = this.schedules.get(scheduleId);
        if (schedule) {
            schedule.filters.push(filter);
        }
    }

    public addGrouping(scheduleId: string, fieldId: string): void {
        const schedule = this.schedules.get(scheduleId);
        if (schedule) {
            schedule.grouping.push({
                fieldId,
                showHeader: true,
                showFooter: true,
                showBlankRow: false
            });
        }
    }

    public addSorting(scheduleId: string, fieldId: string, order: SortOrder = 'Ascending'): void {
        const schedule = this.schedules.get(scheduleId);
        if (schedule) {
            schedule.sorting.push({ fieldId, order });
        }
    }

    // ==================== Schedule Generation ====================

    public generateSchedule(scheduleId: string): ScheduleResult {
        const schedule = this.schedules.get(scheduleId);
        if (!schedule) {
            return this.createEmptyResult(scheduleId);
        }

        const elements = this.elementSource();

        // 1. Filter by category
        let filtered = elements.filter(e => e.category === schedule.category);

        // 2. Apply filters
        filtered = this.applyFilters(filtered, schedule.filters, schedule.fields);

        // 3. Extract field values
        const rows: ScheduleRow[] = filtered.map(element => ({
            elementId: element.id,
            values: this.extractFieldValues(element, schedule.fields)
        }));

        // 4. Apply sorting
        this.applySorting(rows, schedule.sorting, schedule.fields);

        // 5. Apply grouping
        const groupedRows = this.applyGrouping(rows, schedule.grouping, schedule.fields);

        // 6. Calculate totals
        const totals = this.calculateTotals(rows, schedule.fields);

        // 7. Add grand total row if needed
        if (schedule.showGrandTotal) {
            groupedRows.push({
                elementId: '__grand_total__',
                values: totals,
                isGrandTotal: true
            });
        }

        return {
            definition: schedule,
            rows: groupedRows,
            totals,
            rowCount: rows.length,
            generatedAt: Date.now()
        };
    }

    private extractFieldValues(element: any, fields: ScheduleField[]): Record<string, any> {
        const values: Record<string, any> = {};

        for (const field of fields) {
            if (field.isCalculated) {
                values[field.id] = this.evaluateFormula(field, element);
            } else {
                // Try to get from parameters
                let value = element.parameters?.[field.parameterName];

                // Fallback to direct property
                if (value === undefined) {
                    value = element[field.parameterName];
                }

                // Parse numeric strings
                if (field.fieldType === 'Number' || field.fieldType === 'Area' ||
                    field.fieldType === 'Length' || field.fieldType === 'Volume') {
                    value = this.parseNumeric(value);
                }

                values[field.id] = value;
            }
        }

        return values;
    }

    private parseNumeric(value: any): number {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            // Extract number from strings like "5.5 m²" or "$1,234.56"
            const match = value.match(/[\d.,]+/);
            if (match) {
                return parseFloat(match[0].replace(',', ''));
            }
        }
        return 0;
    }

    private evaluateFormula(field: ScheduleField, element: any): any {
        if (field.parameterName === '__count__') {
            return 1;  // Each element counts as 1
        }

        if (!field.formula) return null;

        // Simple formula evaluation
        try {
            let formula = field.formula;

            // Replace parameter references with actual values
            const paramRegex = /\[([^\]]+)\]/g;
            formula = formula.replace(paramRegex, (_, paramName) => {
                const value = element.parameters?.[paramName] ?? element[paramName] ?? 0;
                return typeof value === 'number' ? value.toString() : `${this.parseNumeric(value)}`;
            });

            // Evaluate (using Function for simple math, with safety checks)
            return new Function('return ' + formula)();
        } catch {
            return null;
        }
    }

    private applyFilters(elements: any[], filters: ScheduleFilter[], fields: ScheduleField[]): any[] {
        for (const filter of filters) {
            const field = fields.find(f => f.id === filter.fieldId);
            if (!field) continue;

            elements = elements.filter(element => {
                const value = element.parameters?.[field.parameterName] ?? element[field.parameterName];

                switch (filter.operator) {
                    case 'Equals': return value === filter.value;
                    case 'NotEquals': return value !== filter.value;
                    case 'Contains': return String(value).includes(String(filter.value));
                    case 'GreaterThan': return this.parseNumeric(value) > filter.value;
                    case 'LessThan': return this.parseNumeric(value) < filter.value;
                    case 'Between': {
                        const num = this.parseNumeric(value);
                        return num >= filter.value && num <= filter.value2;
                    }
                    case 'IsEmpty': return value === null || value === undefined || value === '';
                    default: return true;
                }
            });
        }
        return elements;
    }

    private applySorting(rows: ScheduleRow[], sorting: ScheduleSorting[], fields: ScheduleField[]): void {
        rows.sort((a, b) => {
            for (const sort of sorting) {
                const field = fields.find(f => f.id === sort.fieldId);
                if (!field) continue;

                const valA = a.values[sort.fieldId];
                const valB = b.values[sort.fieldId];

                let comparison = 0;
                if (typeof valA === 'number' && typeof valB === 'number') {
                    comparison = valA - valB;
                } else {
                    comparison = String(valA).localeCompare(String(valB));
                }

                if (comparison !== 0) {
                    return sort.order === 'Descending' ? -comparison : comparison;
                }
            }
            return 0;
        });
    }

    private applyGrouping(rows: ScheduleRow[], grouping: ScheduleGrouping[], fields: ScheduleField[]): ScheduleRow[] {
        if (grouping.length === 0) return rows;

        const result: ScheduleRow[] = [];
        const groupField = fields.find(f => f.id === grouping[0].fieldId);
        if (!groupField) return rows;

        // Group by value
        const groups = new Map<any, ScheduleRow[]>();
        for (const row of rows) {
            const key = row.values[grouping[0].fieldId] ?? '__null__';
            if (!groups.has(key)) {
                groups.set(key, []);
            }
            groups.get(key)!.push(row);
        }

        // Build result with headers/footers
        for (const [key, groupRows] of groups) {
            if (grouping[0].showHeader) {
                result.push({
                    elementId: `__group_header_${key}__`,
                    values: { [grouping[0].fieldId]: key },
                    isGroupHeader: true,
                    groupLevel: 0
                });
            }

            result.push(...groupRows);

            if (grouping[0].showFooter) {
                const footerTotals = this.calculateTotals(groupRows, fields);
                result.push({
                    elementId: `__group_footer_${key}__`,
                    values: footerTotals,
                    isGroupFooter: true,
                    groupLevel: 0
                });
            }

            if (grouping[0].showBlankRow) {
                result.push({
                    elementId: `__blank_${key}__`,
                    values: {},
                    groupLevel: 0
                });
            }
        }

        return result;
    }

    private calculateTotals(rows: ScheduleRow[], fields: ScheduleField[]): Record<string, number> {
        const totals: Record<string, number> = {};

        for (const field of fields) {
            if (field.aggregation === 'None') continue;

            const values = rows
                .filter(r => !r.isGroupHeader && !r.isGroupFooter && !r.isGrandTotal)
                .map(r => this.parseNumeric(r.values[field.id]))
                .filter(v => !isNaN(v));

            switch (field.aggregation) {
                case 'Sum':
                    totals[field.id] = values.reduce((a, b) => a + b, 0);
                    break;
                case 'Average':
                    totals[field.id] = values.length > 0
                        ? values.reduce((a, b) => a + b, 0) / values.length
                        : 0;
                    break;
                case 'Count':
                    totals[field.id] = values.length;
                    break;
                case 'Min':
                    totals[field.id] = values.length > 0 ? Math.min(...values) : 0;
                    break;
                case 'Max':
                    totals[field.id] = values.length > 0 ? Math.max(...values) : 0;
                    break;
            }
        }

        return totals;
    }

    private createEmptyResult(scheduleId: string): ScheduleResult {
        return {
            definition: { id: scheduleId } as ScheduleDefinition,
            rows: [],
            totals: {},
            rowCount: 0,
            generatedAt: Date.now()
        };
    }

    // ==================== Getters ====================

    public getSchedule(id: string): ScheduleDefinition | undefined {
        return this.schedules.get(id);
    }

    public getAllSchedules(): ScheduleDefinition[] {
        return Array.from(this.schedules.values());
    }

    // ==================== Export ====================

    public exportToCSV(scheduleId: string): string {
        const result = this.generateSchedule(scheduleId);
        if (result.rows.length === 0) return '';

        const visibleFields = result.definition.fields.filter(f => !f.isHidden);

        // Header row
        const header = visibleFields.map(f => f.name).join(',');

        // Data rows
        const dataRows = result.rows
            .filter(r => !r.isGroupHeader && !r.isGroupFooter)
            .map(row => {
                return visibleFields.map(field => {
                    const value = row.values[field.id];
                    if (value === undefined || value === null) return '';
                    if (typeof value === 'string' && value.includes(',')) {
                        return `"${value}"`;
                    }
                    return String(value);
                }).join(',');
            });

        return [header, ...dataRows].join('\n');
    }

    // ==================== Preset Schedules ====================

    public createWallSchedule(): ScheduleDefinition {
        const schedule = this.createSchedule('Wall Schedule', 'Walls');
        this.addField(schedule, { name: 'Length', parameterName: 'Length', fieldType: 'Length', aggregation: 'Sum' });
        this.addField(schedule, { name: 'Area', parameterName: 'Area', fieldType: 'Area', aggregation: 'Sum' });
        this.addField(schedule, { name: 'Volume', parameterName: 'Volume', fieldType: 'Volume', aggregation: 'Sum' });
        return schedule;
    }

    public createDoorSchedule(): ScheduleDefinition {
        const schedule = this.createSchedule('Door Schedule', 'Doors');
        this.addField(schedule, { name: 'Mark', parameterName: 'Mark', fieldType: 'String' });
        this.addField(schedule, { name: 'Width', parameterName: 'Width', fieldType: 'Length' });
        this.addField(schedule, { name: 'Height', parameterName: 'Height', fieldType: 'Length' });
        this.addField(schedule, { name: 'Level', parameterName: 'Level', fieldType: 'String' });
        return schedule;
    }

    public createMEPSchedule(category: string): ScheduleDefinition {
        const schedule = this.createSchedule(`${category} Schedule`, category);
        this.addField(schedule, { name: 'System Type', parameterName: 'System Type', fieldType: 'String' });
        this.addField(schedule, { name: 'Size', parameterName: 'Size', fieldType: 'String' });
        this.addField(schedule, { name: 'Length', parameterName: 'Length', fieldType: 'Length', aggregation: 'Sum' });
        return schedule;
    }

    // ==================== Key Schedules ====================

    private keySchedules: Map<string, { keyName: string; rows: Map<string, Record<string, any>> }> = new Map();

    public createKeySchedule(definitionId: string, keyParamName: string): void {
        this.keySchedules.set(definitionId, { keyName: keyParamName, rows: new Map() });
    }

    public addKeyRow(definitionId: string, keyValue: string, data: Record<string, any>): void {
        const ks = this.keySchedules.get(definitionId);
        if (ks) {
            ks.rows.set(keyValue, data);
        }
    }

    /**
     * Apply Key values to an element based on its Key Parameter value.
     */
    public applyKey(element: any, definitionId: string): boolean {
        const ks = this.keySchedules.get(definitionId);
        if (!ks) return false;

        // Get value of the Key Parameter from the element
        const keyValue = element.parameters?.[ks.keyName];
        if (!keyValue) return false;

        // Lookup data
        const data = ks.rows.get(keyValue);
        if (!data) return false;

        // Apply data to element parameters
        if (!element.parameters) element.parameters = {};
        Object.assign(element.parameters, data);

        return true;
    }
}
